// frontend/src/modules/onboarding/GetStartedPanel.tsx
//
// Replaces HomePage's old hardcoded checklist. That one was neither
// module- nor permission-aware — it told employees to upload the company logo
// and linked them to a page that 403s — and it could not be dismissed.
//
// This one renders exactly the steps the server says apply to this user, ticks
// them from real data, and can be skipped.

import * as React from 'react';
import { Link } from 'react-router-dom';
import { Check, Sparkles } from 'lucide-react';
import { DarkCard } from '../../design-system/pds';
import { useOnboarding } from './OnboardingProvider';
import { useTour } from './TourProvider';
import { STEP_PRESENTATION } from './registry';

export function GetStartedPanel() {
  const { state, skip, complete, dismissStep } = useOnboarding();
  const tour = useTour();

  // Only steps this build knows how to present. A step added on the server
  // before the client ships its presentation must not be counted in the
  // denominator — the panel would read "1/3" while rendering two rows. The
  // registry drift test makes that a build failure rather than a UI lie, but
  // the count still has to be honest about what it renders.
  const steps = React.useMemo(
    () => (state?.steps ?? []).filter((s) => Boolean(STEP_PRESENTATION[s.key])),
    [state?.steps],
  );
  const doneCount = steps.filter((s) => s.done).length;
  const allDone = steps.length > 0 && doneCount === steps.length;

  // Retire the panel once everything is finished, so it does not come back on
  // the next load.
  React.useEffect(() => {
    if (allDone && state?.status === 'active') void complete();
  }, [allDone, state?.status, complete]);

  if (!state || state.status !== 'active' || steps.length === 0) return null;

  return (
    <DarkCard className="col-span-12 lg:col-span-7" padding="p-7" motionDelay={0.15}>
      <div className="flex items-start justify-between mb-5 gap-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/45">Get set up</p>
          <h3 className="font-semibold text-title-lg mt-1 tracking-[-0.02em] text-white">
            Get started · {doneCount}/{steps.length}
          </h3>
        </div>
        <div className="inline-flex items-center gap-1.5 text-[12px] font-bold text-white px-3 py-1 rounded-full bg-coral shrink-0">
          <Sparkles className="h-3.5 w-3.5" />
          {Math.round((doneCount / steps.length) * 100)}%
        </div>
      </div>

      <ul className="space-y-2">
        {steps.map((s) => {
          const p = STEP_PRESENTATION[s.key];
          return (
            <li key={s.key} className="flex items-center gap-3 py-2.5 px-3 rounded-2xl bg-white/[0.04] ring-1 ring-white/10">
              <span
                aria-hidden
                className={[
                  'inline-flex h-6 w-6 items-center justify-center rounded-full shrink-0',
                  s.done ? 'bg-lime-neon text-ink' : 'bg-white/[0.08] text-white/40 ring-1 ring-white/15',
                ].join(' ')}
              >
                {s.done ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : null}
              </span>
              <span className={['flex-1 text-[13.5px]', s.done ? 'text-white/40 line-through' : 'text-white'].join(' ')}>
                {p.label}
              </span>
              {!s.done && (
                p.stops.length > 0 ? (
                  <button
                    type="button"
                    onClick={() => tour.start(p.stops)}
                    className="text-[12.5px] font-semibold text-white/70 hover:text-white"
                  >
                    Show me
                  </button>
                ) : (
                  <Link
                    to={p.to}
                    onClick={() => { void dismissStep(s.key); }}
                    className="text-[12.5px] font-semibold text-white/70 hover:text-white"
                  >
                    Take a look
                  </Link>
                )
              )}
            </li>
          );
        })}
      </ul>

      <div className="mt-4 flex justify-end">
        <button type="button" onClick={() => { void skip(); }} className="text-[12.5px] text-white/45 hover:text-white/80">
          Skip setup
        </button>
      </div>
    </DarkCard>
  );
}
